import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface ProcessStep {
  step: number;
  title: string;
  description: string;
}

interface CaseStudy {
  title: string;
  client: string;
  industry: string;
  challenge: string;
  solution: string;
  results: {
    metric: string;
    value: string;
  }[];
  image: string;
}

interface Testimonial {
  name: string;
  role: string;
  company: string;
  content: string;
  avatar: string;
}

@Component({
  selector: 'app-rcs',
  imports: [CommonModule, RouterLink],
  templateUrl: './rcs.html',
  styleUrl: './rcs.css',
  standalone: true
})
export class Rcs {
  features: Feature[] = [
    {
      icon: 'M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z',
      title: 'Rich Media Cards',
      description: 'Send stunning image carousels, video messages, and interactive product catalogs. Create visually appealing experiences that drive engagement and conversions.'
    },
    {
      icon: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      title: 'Verified Sender',
      description: 'Display your brand logo, name, and verified status in every message. Build trust with customers through official, authenticated business communication.'
    },
    {
      icon: 'M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122',
      title: 'Suggested Actions',
      description: 'Add quick reply buttons, suggested responses, and call-to-action chips. Guide users through conversations with intuitive, tap-friendly interactions.'
    },
    {
      icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
      title: 'Real-Time Analytics',
      description: 'Track message delivery, read receipts, and user interactions. Measure engagement rates, conversion funnels, and campaign ROI with detailed analytics.'
    },
    {
      icon: 'M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z',
      title: 'In-Message Shopping',
      description: 'Enable product browsing, selection, and purchase directly within the message. Reduce friction with seamless in-conversation commerce experiences.'
    },
    {
      icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      title: 'Smart Scheduling',
      description: 'Schedule campaigns based on user timezone, behavior patterns, and optimal engagement windows. Automate delivery for maximum impact and response rates.'
    }
  ];

  process: ProcessStep[] = [
    {
      step: 1,
      title: 'Brand Registration',
      description: 'Register your business and get verified by carriers. We guide you through the approval process for official RCS sender status.'
    },
    {
      step: 2,
      title: 'Design Rich Messages',
      description: 'Create interactive message templates with our visual builder. Add images, buttons, carousels, and custom branding elements.'
    },
    {
      step: 3,
      title: 'Target Audiences',
      description: 'Segment contacts based on RCS capability, demographics, and behavior. Ensure optimal delivery to RCS-enabled devices.'
    },
    {
      step: 4,
      title: 'Launch & Optimize',
      description: 'Deploy campaigns and track real-time engagement. A/B test message formats and optimize based on performance data.'
    }
  ];

  caseStudy: CaseStudy = {
    title: 'Travel Booking & Customer Engagement',
    client: 'TravelWorld Airlines',
    industry: 'Travel & Hospitality',
    challenge: 'TravelWorld needed to modernize their customer communication beyond plain SMS. They wanted to provide rich booking confirmations, interactive check-in experiences, and personalized travel recommendations while maintaining the convenience of native messaging.',
    solution: 'Deployed HoneyKode\'s RCS platform to send branded, interactive messages throughout the customer journey. Implemented rich cards with flight details, visual boarding passes, destination recommendations, and one-tap actions for check-in and upgrades.',
    results: [
      { metric: 'Message Engagement', value: '89%' },
      { metric: 'Mobile Check-in Rate', value: '3.5x increase' },
      { metric: 'Ancillary Revenue', value: '+$2.4M annually' },
      { metric: 'Customer Satisfaction', value: '92% positive' },
      { metric: 'Click-Through Rate', value: '47%' },
      { metric: 'Support Calls', value: '34% reduction' }
    ],
    image: 'https://images.unsplash.com/photo-1436491865332-7a61a109cc05?w=800&h=500&fit=crop'
  };

  testimonial: Testimonial = {
    name: 'Alexandra Stevens',
    role: 'VP of Customer Experience',
    company: 'TravelWorld Airlines',
    content: 'RCS has revolutionized how we communicate with our passengers. The visual boarding passes are stunning, and customers love the interactive features like one-tap seat selection and meal preferences. We\'ve seen a massive increase in mobile check-ins, which has reduced airport congestion and improved the overall travel experience. The verified brand identity also gives customers confidence that messages are genuinely from us. This technology is the future of customer communication in travel.',
    avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=150&h=150&fit=crop'
  };
}
